'use client';

import { LiveFeedStreamEntry } from '@spikemark/live';
import { useCurrentTime } from '@spikemark/shared-hooks';
import { Pencil, Trash } from 'lucide-react';
import { FC, PropsWithChildren, type JSX } from 'react';
import { Avatar } from '../avatar';
import { CloudinaryImage } from '../cloudinary-image';
import { Mascot } from '../mascot';
import { Button } from '../ui/button';
import { FeedEntryBody } from './feed-entry-body';
import { FeedEntryByline } from './feed-entry-shared';
import { TweetFeedItem } from './tweet';
type FeedProps = {
  data?: LiveFeedStreamEntry[];
  variant?: 'default' | 'compact';
  isAggregated?: boolean;
  isEmpty?: boolean;
  onEntryEdit?: (entry: LiveFeedStreamEntry) => void;
  onEntryDelete?: (entry: LiveFeedStreamEntry) => void;
};
export const FeedCard: FC<{
  entry: LiveFeedStreamEntry;
  variant?: 'default' | 'compact';
  isAggregated?: boolean;
  baseDate: Date;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({
  entry,
  variant = 'default',
  baseDate,
  isAggregated,
  onEdit,
  onDelete
}) => {
  const actions = onEdit || onDelete ? <div className="absolute top-0 right-[-80px] w-[80px]">
        <div className="inline-flex items-center rounded-full bg-gray-100">
          {onEdit && <Button size="icon" variant="ghost" onClick={onEdit}>
              <Pencil size={16} />
            </Button>}
          {onDelete && <Button size="icon" variant="ghost" onClick={onDelete}>
              <Trash size={16} />
            </Button>}
        </div>
      </div> : null;
  let content: JSX.Element | null = null;
  if (entry.source === 'twitter' && entry.social_post_id) {
    content = <div className="text-base">
        <TweetFeedItem tweetId={entry.social_post_id} tournamentId={entry.tournament_id} tournamentName={entry.tournament_name} baseDate={baseDate} isAggregated={isAggregated} />
        {actions}
      </div>;
  } else {
    const body = <div className="flex-1 space-y-2 @md:space-y-3 @lg:space-y-4">
        {entry.title && <h2 className="font-bold text-base @md:text-lg @lg:text-xl" dangerouslySetInnerHTML={{
        __html: entry.title.replaceAll(/\<a/g, '<a class="text-navy-oxford underline" ')
      }} />}
        {entry.image_path && variant !== 'compact' ? <CloudinaryImage className="w-full mx-auto aspect-video rounded overflow-clip" alt={entry.title ?? ''} src={`payload/${entry.image_path}`} fill /> : null}
        {variant === 'default' || !entry.title ? <FeedEntryBody richText={entry.rich_text as string} /> : null}
      </div>;
    content = <div className="pt-1 pb-1 @md:pb-2 @lg:pb-4">
        <FeedEntryByline baseDate={baseDate} entryDate={new Date(entry.created_at!)} tournamentId={entry.tournament_id} tournamentName={entry.tournament_name} showTournament={isAggregated} />
        {entry.team_id && (!entry.image_path || variant === 'compact') ? <div className="flex">
            <div className="flex-1">{body}</div>
            <div className="ml-4">
              {entry.player_id ? <Avatar playerName={entry.player_name!} schoolName={entry.team_name!} schoolLogoThumbnail={entry.team_logo ?? ''} userPicture={entry.photo ?? ''} variant="print" /> : <Mascot alt={entry.team_name!} path={entry.team_logo ?? ''} variant="print" displayMode="team" />}
            </div>
          </div> : body}
        {actions}
      </div>;
  }
  return <div className="flex mb-2 @lg:mb-4 relative border-b border-gray-200 last:border-b-0 text-base pb-2 @lg:pb-4" data-sentry-component="FeedCard" data-sentry-source-file="feed.tsx">
      {isAggregated && <div>
          <Mascot alt={entry.tournament_name!} path={entry.tournament_logo ?? ''} variant="medium" displayMode="team" className="mr-4" />
        </div>}
      <div className="flex-1">{content}</div>
    </div>;
};
export const FeedList: FC<FeedProps & {
  baseDate: Date;
}> = ({
  data,
  variant,
  baseDate,
  isAggregated,
  onEntryEdit,
  onEntryDelete
}) => {
  return <>
      {data?.map(item => <FeedCard entry={item} variant={variant} isAggregated={isAggregated} key={item.entry_id} baseDate={baseDate} onEdit={onEntryEdit ? () => onEntryEdit(item) : undefined} onDelete={onEntryDelete ? () => onEntryDelete(item) : undefined} />)}
    </>;
};
export const FeedBody: FC<PropsWithChildren<Pick<FeedProps, 'isAggregated' | 'isEmpty'>>> = ({
  isEmpty,
  isAggregated,
  children
}) => {
  return <div className="@container scoreboard-tweet react-tweet-theme light rounded-lg p-3 lg:p-4 shadow-sm bg-white" data-theme="light" data-sentry-component="FeedBody" data-sentry-source-file="feed.tsx">
      {isEmpty ? <p>
          {isAggregated ? 'Updates will appear here once the first live tournament begins.' : 'Updates will appear here once the tournament begins.'}
        </p> : children}
    </div>;
};
export const Feed: FC<FeedProps> = props => {
  return <FeedBody {...props} data-sentry-element="FeedBody" data-sentry-component="Feed" data-sentry-source-file="feed.tsx">
      <FeedList {...props} baseDate={useCurrentTime()} data-sentry-element="FeedList" data-sentry-source-file="feed.tsx" />
    </FeedBody>;
};